import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { adminApi } from "services/api";
import { useSocket } from "context/SocketContext";
import { lightDark } from "utils/lightDark";

function NewConversationDialog({ open, onClose, onStarted }) {
  const [channel, setChannel] = useState("sms");
  const [customerPhone, setCustomerPhone] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setChannel("sms");
    setCustomerPhone("");
    setText("");
    setError(null);
  };

  const handleClose = () => {
    if (sending) return;
    reset();
    onClose();
  };

  const handleSend = async () => {
    if (!customerPhone.trim() || !text.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await adminApi.startChatConversation(channel, customerPhone.trim(), text.trim());
      reset();
      onClose();
      onStarted(res.conversation._id);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to start conversation");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>New conversation</DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={channel}
            onChange={(e, value) => value && setChannel(value)}
          >
            <ToggleButton value="sms">SMS</ToggleButton>
            <ToggleButton value="whatsapp">WhatsApp</ToggleButton>
          </ToggleButtonGroup>

          {channel === "whatsapp" && (
            <Alert severity="warning">
              Twilio only allows a first WhatsApp message to someone who has never messaged you
              before if you use a pre-approved template — a free-form message like this will be
              rejected. Use SMS to start a brand-new conversation, or WhatsApp only for a customer
              who has already messaged in.
            </Alert>
          )}

          <TextField
            label="Phone number"
            placeholder="+2348012345678"
            fullWidth
            size="small"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
          />
          <TextField
            label="Message"
            placeholder="Type the first message…"
            fullWidth
            multiline
            minRows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleClose} disabled={sending}>
          Cancel
        </MDButton>
        <MDButton
          color="info"
          variant="gradient"
          onClick={handleSend}
          disabled={sending || !customerPhone.trim() || !text.trim()}
        >
          {sending ? <CircularProgress size={18} color="inherit" /> : "Send"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

NewConversationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStarted: PropTypes.func.isRequired,
};

function timeAgo(date) {
  const diffMs = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function ConversationList({ selectedId, onSelect }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const socket = useSocket();

  const fetchConversations = useCallback(async () => {
    try {
      const res = await adminApi.getChatConversations({ limit: 100 });
      setConversations(res.conversations || []);
    } catch {
      // leave the existing list in place — a transient fetch failure shouldn't blank the inbox
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleMessage = ({ conversation }) => {
      setConversations((prev) => [conversation, ...prev.filter((c) => c._id !== conversation._id)]);
    };
    const handleRead = ({ conversationId }) => {
      setConversations((prev) =>
        prev.map((c) => (c._id === conversationId ? { ...c, unreadCount: 0 } : c))
      );
    };
    socket.on("chat:message", handleMessage);
    socket.on("chat:read", handleRead);
    return () => {
      socket.off("chat:message", handleMessage);
      socket.off("chat:read", handleRead);
    };
  }, [socket]);

  const filtered = conversations.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (c.displayName || "").toLowerCase().includes(q) || c.customerPhone.includes(q);
  });

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <MDBox p={2} display="flex" alignItems="center" gap={1}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search name or phone…"
          // 16px text on phones stops iOS Safari zooming the page in when the field is focused
          sx={({ breakpoints }) => ({
            [breakpoints.down("md")]: { "& .MuiInputBase-input": { fontSize: "1rem" } },
          })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Tooltip title="Start a new conversation">
          <IconButton color="info" onClick={() => setNewDialogOpen(true)}>
            <Icon>add_comment</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
      <Divider />
      <MDBox sx={{ overflowY: "auto", flex: 1 }}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </MDBox>
        ) : filtered.length === 0 ? (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="body2" color="text">
              {search.trim() ? "No conversations match your search" : "No conversations yet"}
            </MDTypography>
          </MDBox>
        ) : (
          filtered.map((c) => (
            <MDBox
              key={c._id}
              onClick={() => onSelect(c._id)}
              sx={{
                px: 2,
                py: 1.5,
                cursor: "pointer",
                backgroundColor: selectedId === c._id ? "rgba(25,118,210,0.12)" : "transparent",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                borderBottom: lightDark(
                  "1px solid rgba(0,0,0,0.06)",
                  "1px solid rgba(255, 255, 255, 0.08)"
                ),
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                <MDTypography variant="button" fontWeight={c.unreadCount > 0 ? "bold" : "regular"}>
                  {c.displayName || c.customerPhone}
                </MDTypography>
                <Chip
                  label={c.channel === "whatsapp" ? "WhatsApp" : "SMS"}
                  size="small"
                  color={c.channel === "whatsapp" ? "success" : "info"}
                  sx={{ fontSize: "0.65rem", height: 20 }}
                />
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  // phones: use the full row width (minus the time); desktop keeps the 180px cap
                  sx={{
                    maxWidth: { xs: "none", md: 180 },
                    flex: { xs: 1, md: "0 1 auto" },
                    minWidth: 0,
                  }}
                >
                  {c.lastMessagePreview}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {timeAgo(c.lastMessageAt)}
                </MDTypography>
              </MDBox>
              {c.unreadCount > 0 && (
                <Chip
                  label={c.unreadCount}
                  size="small"
                  color="error"
                  sx={{ mt: 0.5, height: 18, fontSize: "0.65rem" }}
                />
              )}
            </MDBox>
          ))
        )}
      </MDBox>
      <NewConversationDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onStarted={onSelect}
      />
    </Card>
  );
}

ConversationList.propTypes = {
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

ConversationList.defaultProps = {
  selectedId: null,
};

export default ConversationList;
